package com.banyan.flightviewer.web.components

import com.banyan.flightviewer.web.components.aircraft.AircraftFilters
import com.banyan.flightviewer.web.dom.bindFlow
import com.banyan.flightviewer.web.dom.button
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.onClick
import com.banyan.flightviewer.web.state.WebAppContainer
import kotlinx.browser.document
import org.w3c.dom.Element
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event

/**
 * Filter panel shown above the aircraft table when the user clicks the
 * filter icon in the TopBar. Contains altitude range (two range sliders),
 * ground/airborne checkboxes, an airline-prefix text input, and reset/close
 * buttons.
 *
 * Mirrors [FilterPanel.kt](desktop/src/main/kotlin/com/banyan/flightviewer/ui/FilterPanel.kt)
 * from desktop. Two-slider range is used instead of a true range slider
 * because HTML has no native range-with-two-knobs control; guards prevent
 * min > max.
 */
fun mountFilterPanel(parent: Element, app: WebAppContainer) {
    val container = parent.div(className = "fv-filter")
    container.asDynamic().style.display = "none"

    // ── Header row ─────────────────────────────────────────────────────
    val header = container.div(className = "fv-filter__header")
    header.div(className = "fv-filter__title") { textContent = "Filters" }
    header.div(className = "fv-filter__spacer") { }

    val resetBtn = header.button(className = "fv-filter__reset") {
        textContent = "Reset"
    }
    resetBtn.onClick { app.uiState.resetFilters() }

    val closeBtn = header.button(className = "fv-filter__close") {
        textContent = "\u2715"
    }
    closeBtn.onClick { app.uiState.setFiltersVisible(false) }

    // ── Altitude range ─────────────────────────────────────────────────
    val altLabel = container.div(className = "fv-filter__label") {
        textContent = "Altitude: 0 - 60k"
    }

    val altRow = container.div(className = "fv-filter__range-row")

    val altMinInput = document.createElement("input") as HTMLInputElement
    altMinInput.type = "range"
    altMinInput.className = "fv-filter__range"
    altMinInput.min = "0"
    altMinInput.max = "60000"
    altMinInput.step = "1000"
    altRow.appendChild(altMinInput)

    val altMaxInput = document.createElement("input") as HTMLInputElement
    altMaxInput.type = "range"
    altMaxInput.className = "fv-filter__range"
    altMaxInput.min = "0"
    altMaxInput.max = "60000"
    altMaxInput.step = "1000"
    altRow.appendChild(altMaxInput)

    // "change" fires when the knob is released; "input" fires during drag.
    // We commit on change (and merge the current min/max so the pair moves
    // together), but update the label live via `input` so the user sees
    // what they're doing.
    val commitAltitude = {
        val rawMin = altMinInput.value.toIntOrNull() ?: 0
        val rawMax = altMaxInput.value.toIntOrNull() ?: 60_000
        val lo = minOf(rawMin, rawMax)
        val hi = maxOf(rawMin, rawMax)
        app.uiState.setFilters(app.uiState.filters.value.copy(altMin = lo, altMax = hi))
    }
    val updateAltLabel = {
        val rawMin = altMinInput.value.toIntOrNull() ?: 0
        val rawMax = altMaxInput.value.toIntOrNull() ?: 60_000
        val lo = minOf(rawMin, rawMax)
        val hi = maxOf(rawMin, rawMax)
        altLabel.textContent = "Altitude: ${formatAlt(lo)} - ${formatAlt(hi)}"
    }
    altMinInput.addEventListener("input", { _: Event -> updateAltLabel() })
    altMaxInput.addEventListener("input", { _: Event -> updateAltLabel() })
    altMinInput.addEventListener("change", { _: Event -> commitAltitude() })
    altMaxInput.addEventListener("change", { _: Event -> commitAltitude() })

    // ── Ground / airborne ─────────────────────────────────────────────
    val toggleRow = container.div(className = "fv-filter__toggles")
    val groundCb = checkbox(toggleRow, "Ground") { checked ->
        app.uiState.setFilters(app.uiState.filters.value.copy(showGround = checked))
    }
    val airborneCb = checkbox(toggleRow, "Airborne") { checked ->
        app.uiState.setFilters(app.uiState.filters.value.copy(showAirborne = checked))
    }

    // ── Airline prefix ────────────────────────────────────────────────
    val airlineInput = document.createElement("input") as HTMLInputElement
    airlineInput.type = "text"
    airlineInput.className = "fv-filter__airline"
    airlineInput.placeholder = "Airline prefix (e.g. DAL, UAL)"
    airlineInput.autocomplete = "off"
    airlineInput.addEventListener("input", { _: Event ->
        app.uiState.setFilters(app.uiState.filters.value.copy(airlinePrefix = airlineInput.value))
    })
    container.appendChild(airlineInput)

    // ── Reactive wiring ──────────────────────────────────────────────
    // Initialise + reflect external changes (e.g. Reset button) back into the
    // inputs. Guard against setting values that would re-fire events and loop.
    app.appState.scope.bindFlow(app.uiState.filters) { f ->
        if (altMinInput.value != f.altMin.toString()) altMinInput.value = f.altMin.toString()
        if (altMaxInput.value != f.altMax.toString()) altMaxInput.value = f.altMax.toString()
        altLabel.textContent = "Altitude: ${formatAlt(f.altMin)} - ${formatAlt(f.altMax)}"
        if (groundCb.checked != f.showGround) groundCb.checked = f.showGround
        if (airborneCb.checked != f.showAirborne) airborneCb.checked = f.showAirborne
        if (airlineInput.value != f.airlinePrefix) airlineInput.value = f.airlinePrefix
        // Hide reset button when no filter is active.
        resetBtn.asDynamic().style.visibility = if (f.isActive) "visible" else "hidden"
    }

    // Show/hide the panel in response to uiState.filtersVisible.
    app.appState.scope.bindFlow(app.uiState.filtersVisible) { visible ->
        container.asDynamic().style.display = if (visible) "block" else "none"
    }
}

private fun checkbox(
    parent: Element,
    label: String,
    onChange: (Boolean) -> Unit,
): HTMLInputElement {
    val lbl = document.createElement("label") as org.w3c.dom.HTMLLabelElement
    lbl.className = "fv-filter__checkbox"
    val cb = document.createElement("input") as HTMLInputElement
    cb.type = "checkbox"
    cb.checked = true
    cb.addEventListener("change", { _: Event -> onChange(cb.checked) })
    lbl.appendChild(cb)
    val span = document.createElement("span")
    span.textContent = label
    lbl.appendChild(span)
    parent.appendChild(lbl)
    return cb
}

private fun formatAlt(ft: Int): String = when {
    ft >= 1000 -> "${ft / 1000}k"
    else -> "$ft"
}
